import type {
  Availability,
  EffectiveRoster,
  Fill,
  GroupConfig,
  IsoDate,
  Override,
  Player,
  PlayerId,
  PlayerStatus,
  RosterSlot,
  Session,
  SittingSlot,
} from './types.js';

/** Doubles. The number of court slots a session has to fill. */
export const PLAY_SLOTS = 4;

/**
 * Court slots nobody in the group can cover, because too few were available when the season
 * was generated (§9.1). Someone has to find a player from outside the group before the night,
 * so every screen that shows the session flags it.
 */
export function slotsNeeded(session: Session): number {
  if (session.status !== 'scheduled') return 0;
  return Math.max(0, PLAY_SLOTS - session.playing.length);
}

/** True when the schedule left this session a player short, whether or not a name has been found. */
export function isShortHanded(session: Session): boolean {
  return slotsNeeded(session) > 0;
}

/** This date's recorded fills, lowest slot first — the order they show up in the lineup. */
export function fillsFor(fills: Fill[], date: IsoDate): Fill[] {
  return fills.filter((f) => f.date === date).sort((a, b) => a.slot - b.slot);
}

/**
 * Empty court slots with nobody named for them yet: what the schedule left short (§9.1) minus
 * the fills recorded since. This is the number the UI counts placeholders with, and the one
 * that says whether there is still anything to record.
 */
export function openSlots(session: Session, fills: Fill[] = []): number {
  return Math.max(0, slotsNeeded(session) - fillsFor(fills, session.date).length);
}

/**
 * The slot a new fill should take: the lowest empty one nobody has named yet, or `undefined`
 * when the night is full. Slots are indices rather than a running count so the write can be
 * conditional — two people recording a name at once race for slot 0 and one of them loses,
 * instead of both appending and putting five on court.
 */
export function nextOpenSlot(session: Session, fills: Fill[] = []): number | undefined {
  const taken = new Set(fillsFor(fills, session.date).map((f) => f.slot));
  for (let slot = 0; slot < slotsNeeded(session); slot += 1) {
    if (!taken.has(slot)) return slot;
  }
  return undefined;
}

export function playerMap(players: Player[]): Map<PlayerId, Player> {
  return new Map(players.map((p) => [p.id, p]));
}

export function playerName(players: Map<PlayerId, Player>, id: PlayerId): string {
  return players.get(id)?.name ?? id;
}

/**
 * Is this player coming? Three sources, most recent first:
 *
 * 1. their own answer for this date, which is the only one they control;
 * 2. failing that, an override in their name — arranging a sub *is* saying you can't make it;
 * 3. failing that, the August grid (`session.unavailable`), which is a forecast (§6.1).
 *
 * The order is what makes "I'm available now" a swap rather than an undo: it writes (1) `true`,
 * which outranks the override left standing at (2), so the sub keeps the slot and the player
 * rejoins the reserves.
 */
export function isUnavailable(
  session: Session,
  overrides: Override[],
  availabilities: Availability[],
  playerId: PlayerId,
): boolean {
  const own = availabilities.find((a) => a.date === session.date && a.playerId === playerId);
  if (own) return !own.available;
  if (overrides.some((o) => o.date === session.date && o.outPlayerId === playerId)) return true;
  return session.unavailable.includes(playerId);
}

function subSlot(
  players: Map<PlayerId, Player>,
  override: Override,
  outPlayerId: PlayerId,
): RosterSlot {
  const name =
    override.subKind === 'reserve' && override.subPlayerId
      ? playerName(players, override.subPlayerId)
      : (override.subName ?? 'TBD');
  const slot: RosterSlot = {
    name,
    inForPlayerId: outPlayerId,
    inForName: playerName(players, outPlayerId),
  };
  if (override.subKind === 'reserve' && override.subPlayerId) {
    slot.playerId = override.subPlayerId;
  }
  return slot;
}

/**
 * Available first, and each source's own order within either half (§6.1). Anyone who isn't
 * coming can still be asked — `unavailable` is an annotation, never a filter — but they belong
 * under the people who can play, so the names worth trying are the ones read first. The sort is
 * stable, so the order underneath is the one the caller built.
 */
function availableFirst<T extends { unavailable: boolean }>(slots: T[]): T[] {
  return [...slots].sort((a, b) => Number(a.unavailable) - Number(b.unavailable));
}

/**
 * `playing − outPlayers + subs + fills`, derived and never stored (§3). Subs take the slot of
 * the player they replaced so the order on screen and in the reminder text stays stable; fills
 * have no slot to take, so they land after the scheduled four in slot order.
 */
export function effectiveRoster(
  group: GroupConfig,
  session: Session,
  overrides: Override[],
  availabilities: Availability[] = [],
  fills: Fill[] = [],
): EffectiveRoster {
  const players = playerMap(group.players);
  const forDate = overrides.filter((o) => o.date === session.date);
  const byOutPlayer = new Map(forDate.map((o) => [o.outPlayerId, o]));

  const playing: RosterSlot[] = [];
  const out: EffectiveRoster['out'] = [];

  for (const id of session.playing) {
    const override = byOutPlayer.get(id);
    if (!override) {
      playing.push({ playerId: id, name: playerName(players, id) });
      continue;
    }
    const sub = subSlot(players, override, id);
    playing.push(sub);
    out.push({ playerId: id, name: playerName(players, id), sub });
  }

  // A fill is an extra body, not a swap, so it appends rather than replacing anything — which
  // also keeps the scheduled names in the same place whether or not one has been found yet.
  for (const fill of fillsFor(fills, session.date)) {
    playing.push({ name: fill.name });
  }

  const subbedInReserves = new Set(
    forDate
      .filter((o) => o.subKind === 'reserve' && o.subPlayerId)
      .map((o) => o.subPlayerId as PlayerId),
  );

  const slotFor = (id: PlayerId): SittingSlot => ({
    playerId: id,
    name: playerName(players, id),
    unavailable: isUnavailable(session, forDate, availabilities, id),
    subbingIn: subbedInReserves.has(id),
  });

  const sitting: SittingSlot[] = session.sitting.map(slotFor);

  // Available first, so the card reads as a list of who could actually be asked. Roster order
  // underneath rather than either source's order, so a player's place among the others who
  // gave the same answer doesn't move about as subs are arranged around them.
  const onCourt = new Set(playing.map((slot) => slot.playerId).filter(Boolean));
  const reserves: SittingSlot[] = availableFirst(
    group.players
      .filter(
        (p) =>
          !onCourt.has(p.id) &&
          (session.playing.includes(p.id) || session.sitting.includes(p.id)),
      )
      .map((p) => slotFor(p.id)),
  );

  const roster: EffectiveRoster = {
    date: session.date,
    status: session.status,
    time: session.time ?? group.startTime,
    playing,
    // Every sub takes a departing player's slot, so overrides can't change the count; only the
    // fills recorded against the schedule's empty slots can.
    needed: openSlots(session, fills),
    sitting,
    reserves,
    out,
  };
  if (session.note) roster.note = session.note;
  return roster;
}

/** Where one player stands in a session, after overrides. */
export function playerStatus(
  session: Session,
  overrides: Override[],
  playerId: PlayerId,
): PlayerStatus {
  const forDate = overrides.filter((o) => o.date === session.date);
  if (forDate.some((o) => o.outPlayerId === playerId)) return 'out';
  if (session.playing.includes(playerId)) return 'playing';
  if (
    forDate.some((o) => o.subKind === 'reserve' && o.subPlayerId === playerId)
  ) {
    return 'subbing';
  }
  if (session.sitting.includes(playerId)) return 'sitting';
  return 'not-in-session';
}

/** A player counts as on court if they are scheduled and haven't dropped, or they're subbing in. */
export function isOnCourt(status: PlayerStatus): boolean {
  return status === 'playing' || status === 'subbing';
}

export interface ReserveOption {
  playerId: PlayerId;
  name: string;
  /** Said in August they couldn't come — rendered de-emphasized, never a filter (§6.1). */
  unavailable: boolean;
}

/**
 * The reserves offered as subs (§6.1). Both sitters normally appear, `unavailable` and all;
 * the only removals are hard facts — a reserve already committed as someone else's sub that
 * night, a player who has already declared themselves out, and the player dropping out.
 */
export function subOptions(
  group: GroupConfig,
  session: Session,
  overrides: Override[],
  outPlayerId: PlayerId,
  availabilities: Availability[] = [],
): ReserveOption[] {
  const players = playerMap(group.players);
  const forDate = overrides.filter((o) => o.date === session.date);
  const committed = new Set(
    forDate
      .filter((o) => o.subKind === 'reserve' && o.subPlayerId)
      .map((o) => o.subPlayerId as PlayerId),
  );
  const droppedOut = new Set(forDate.map((o) => o.outPlayerId));
  // A reserve who has said, this week, that they aren't coming is a hard fact like a cascade,
  // not the August forecast — offering them would be offering someone who already answered no.
  const declined = new Set(
    availabilities
      .filter((a) => a.date === session.date && !a.available)
      .map((a) => a.playerId),
  );

  return availableFirst(
    session.sitting
      .filter(
        (id) =>
          id !== outPlayerId && !committed.has(id) && !droppedOut.has(id) && !declined.has(id),
      )
      .map((id) => ({
        playerId: id,
        name: playerName(players, id),
        unavailable: isUnavailable(session, forDate, availabilities, id),
      })),
  );
}

/**
 * What the ⋯ menu on a match card offers the signed-in player, derived from the two facts that
 * move independently — are they coming, and is their slot covered:
 *
 * | Coming | Slot | Menu |
 * |---|---|---|
 * | yes | theirs, uncovered | name a sub, so declaring themselves out leaves four on court |
 * | yes | somebody else's, as their sub | name a replacement, for the same reason |
 * | yes | none (a reserve) | say they aren't available |
 * | yes | covered by a sub | cancel the sub to take it back, or say they aren't available |
 * | no  | covered by a sub | cancel the sub (a full undo), or say they're available after all |
 * | no  | none (a reserve) | say they're available after all |
 *
 * One rule runs down the "Coming: yes" rows: **you cannot drop out of a court slot without
 * arranging cover** (§3). Which slot it is decides who the override ends up naming — your own,
 * or the one you were covering for somebody else — but not whether a sub is required.
 *
 * `add-sub` is the one item that isn't about the signed-in player at all: it is offered to
 * everyone in the session, in whatever position, for as long as the night has an empty slot
 * left (§9.1). Whoever found the body is the one who records the name, and that is as likely
 * to be a sitter as one of the four. It comes last for the same reason — it answers a question
 * about the night, under the items that answer questions about you.
 *
 * Empty only for a `no-session` date, or for somebody who isn't in the session at all.
 */
export type CardAction =
  | 'name-sub'
  | 'name-replacement'
  | 'cancel-sub'
  | 'mark-unavailable'
  | 'mark-available'
  | 'add-sub';

export function cardActions(
  session: Session,
  overrides: Override[],
  availabilities: Availability[],
  playerId: PlayerId,
  fills: Fill[] = [],
): CardAction[] {
  if (session.status !== 'scheduled') return [];
  const status = playerStatus(session, overrides, playerId);
  if (status === 'not-in-session') return [];

  const forDate = overrides.filter((o) => o.date === session.date);
  const actions: CardAction[] = [];

  if (status === 'subbing') {
    // A sub who can't make it after all has somebody else's slot to fill, not their own — so
    // they hand the commitment on rather than cancelling it, and the slot stays covered.
    actions.push('name-replacement');
  } else {
    const unavailable = isUnavailable(session, forDate, availabilities, playerId);
    if (status === 'out') actions.push('cancel-sub');
    if (unavailable) actions.push('mark-available');
    else if (status === 'playing') actions.push('name-sub');
    else actions.push('mark-unavailable');
  }

  if (openSlots(session, fills) > 0) actions.push('add-sub');
  return actions;
}

export function findSession(sessions: Session[], date: IsoDate): Session | undefined {
  return sessions.find((s) => s.date === date);
}

/** Sessions in date order, defensively — callers rely on this for "next up". */
export function sortSessions(sessions: Session[]): Session[] {
  return [...sessions].sort((a, b) => a.date.localeCompare(b.date));
}
